import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Message from '../../components/ui/Message';
import {
    costingApi, CRC, currencySymbol, formatCost, formatMoney, previewPurchase, purchaseError, PurchaseLineForm, USD,
} from './costing';

const emptyLine = (): PurchaseLineForm => ({ productId: '', quantity: '', unitCost: '', tax: '' });

// Compra al proveedor: cada línea con cantidad y costo neto; "Otros costos" (flete, aranceles, seguro) se reparte
// por valor entre las líneas. El IVA/ITBMS recuperable se anota pero no entra al costo.
export const PurchasePage = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<any[]>([]);
    const [suppliers, setSuppliers] = useState<{ supplierCode: number; name: string }[]>([]);
    const [companyCurrency, setCompanyCurrency] = useState<number>(CRC);

    const [supplierId, setSupplierId] = useState<string>('');
    const [supplierInvoice, setSupplierInvoice] = useState('');
    const [invoiceDate, setInvoiceDate] = useState('');
    const [currency, setCurrency] = useState<number>(CRC);
    const [exchangeRate, setExchangeRate] = useState('');
    const [otherCosts, setOtherCosts] = useState('');
    const [note, setNote] = useState('');
    const [lines, setLines] = useState<PurchaseLineForm[]>([emptyLine()]);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        costingApi.products().then((r) => setProducts((r.data?.stocks ?? []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name)))));
        costingApi.suppliers().then((r) => setSuppliers(r.data?.suppliers ?? []));
        costingApi.configuration().then((r) => {
            const c = Number(r.data?.currency) === USD ? USD : CRC;
            setCompanyCurrency(c);
            setCurrency(c);
        });
    }, []);

    const preview = useMemo(() => previewPurchase(lines, otherCosts), [lines, otherCosts]);

    const setLine = (index: number, field: keyof PurchaseLineForm, value: string) =>
        setLines((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: field === 'productId' ? (value ? Number(value) : '') : value } : l)));

    const save = async () => {
        const problem = purchaseError({ lines, currency, companyCurrency, exchangeRate, otherCosts });
        if (problem) {
            setError(problem);
            return;
        }
        setSaving(true);
        const resp = await costingApi.createPurchase({
            supplierId: supplierId ? Number(supplierId) : null,
            supplierInvoice, invoiceDate: invoiceDate || null, currency,
            exchangeRate: currency === companyCurrency ? 1 : Number(exchangeRate),
            otherCosts: Number(otherCosts || 0), note,
            lines: lines.map((l) => ({ productId: Number(l.productId), quantity: Number(l.quantity), unitCost: Number(l.unitCost), tax: Number(l.tax || 0) })),
        });
        setSaving(false);
        if (resp.error) setError(resp.error);
        else setSuccess(`Se registró la compra ${resp.data?.purchaseId}. El inventario y el costo promedio de los productos se actualizaron.`);
    };

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Nueva compra</h3>
                    <p className="text-muted mb-0">Registre lo que compró y cuánto pagó. El costo promedio se calcula solo.</p>
                </div>
                <button type="button" className="btn btn-outline-secondary mt-2 mt-md-0" onClick={() => navigate('/stock-purchases')}>
                    <i className="bi bi-arrow-left me-1" /> Volver a compras
                </button>
            </div>

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body row g-3">
                    <div className="col-md-4">
                        <label htmlFor="supplierSelect" className="form-label">Proveedor</label>
                        <select id="supplierSelect" className="form-select" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                            <option value="">Sin proveedor</option>
                            {suppliers.map((s) => <option key={s.supplierCode} value={s.supplierCode}>{s.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="supplierInvoiceInput" className="form-label">Factura del proveedor</label>
                        <input id="supplierInvoiceInput" className="form-control" maxLength={50} value={supplierInvoice} onChange={(e) => setSupplierInvoice(e.target.value)} />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="invoiceDateInput" className="form-label">Fecha de la factura</label>
                        <input id="invoiceDateInput" type="date" className="form-control" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="currencySelect" className="form-label">Moneda de la factura</label>
                        <select id="currencySelect" className="form-select" value={currency} onChange={(e) => setCurrency(Number(e.target.value))}>
                            <option value={CRC}>Colones (₡)</option>
                            <option value={USD}>Dólares ($)</option>
                        </select>
                    </div>
                    {currency !== companyCurrency && (
                        <div className="col-md-4">
                            <label htmlFor="exchangeRateInput" className="form-label">Tipo de cambio *</label>
                            <input id="exchangeRateInput" type="number" min="0" step="0.01" className="form-control" value={exchangeRate}
                                onChange={(e) => setExchangeRate(e.target.value)} placeholder="Colones por dólar" />
                        </div>
                    )}
                    <div className="col-md-4">
                        <label htmlFor="otherCostsInput" className="form-label">Otros costos</label>
                        <input id="otherCostsInput" type="number" min="0" step="0.01" className="form-control" value={otherCosts}
                            onChange={(e) => setOtherCosts(e.target.value)} placeholder="Flete, aranceles, seguro" />
                        <div className="form-text">Se reparte entre los productos según el valor de cada línea.</div>
                    </div>
                    <div className="col-12">
                        <label htmlFor="purchaseNoteInput" className="form-label">Nota</label>
                        <input id="purchaseNoteInput" className="form-control" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th style={{ minWidth: 220 }}>Producto</th>
                                    <th style={{ width: 120 }}>Cantidad</th>
                                    <th style={{ width: 150 }}>Costo unitario neto</th>
                                    <th style={{ width: 130 }}>IVA recuperable</th>
                                    <th className="text-end">Valor</th>
                                    <th className="text-end">Otros costos</th>
                                    <th className="text-end">Costo final unitario</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((line, i) => (
                                    <tr key={i}>
                                        <td>
                                            <select aria-label={`Producto de la línea ${i + 1}`} className="form-select form-select-sm" value={line.productId}
                                                onChange={(e) => setLine(i, 'productId', e.target.value)}>
                                                <option value="">Elija un producto</option>
                                                {products.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
                                            </select>
                                        </td>
                                        <td><input aria-label={`Cantidad de la línea ${i + 1}`} type="number" min="0" step="any" className="form-control form-control-sm" value={line.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} /></td>
                                        <td><input aria-label={`Costo de la línea ${i + 1}`} type="number" min="0" step="any" className="form-control form-control-sm" value={line.unitCost} onChange={(e) => setLine(i, 'unitCost', e.target.value)} /></td>
                                        <td><input aria-label={`IVA de la línea ${i + 1}`} type="number" min="0" step="any" className="form-control form-control-sm" value={line.tax} onChange={(e) => setLine(i, 'tax', e.target.value)} /></td>
                                        <td className="text-end">{formatMoney(preview.lines[i].value, currency)}</td>
                                        <td className="text-end">{formatMoney(preview.lines[i].otherCosts, currency)}</td>
                                        <td className="text-end fw-semibold">{formatCost(preview.lines[i].finalUnitCost, currency)}</td>
                                        <td className="text-end">
                                            <button type="button" className="btn btn-sm btn-outline-danger" title="Quitar línea" disabled={lines.length === 1}
                                                onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}>
                                                <i className="bi bi-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines((prev) => [...prev, emptyLine()])}>
                            <i className="bi bi-plus-lg me-1" /> Agregar producto
                        </button>
                        <div className="fw-semibold">Total de la compra: {formatMoney(preview.total, currency)}</div>
                    </div>
                    {currency !== companyCurrency && (
                        <p className="text-muted small mt-2 mb-0">
                            Los costos se convierten a {currencySymbol(companyCurrency) === '$' ? 'dólares' : 'colones'} con el tipo de cambio indicado.
                        </p>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mb-5">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/stock-purchases')} disabled={saving}>Cancelar</button>
                <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</> : 'Registrar compra'}
                </button>
            </div>

            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Exitoso" message={success} closeDialog={() => navigate('/stock-purchases')} handleOk={() => navigate('/stock-purchases')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
